"use client";

import { usePostHog } from "posthog-js/react";
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { type PublicAuthState, usePublicAuthState } from "@/components/auth/PublicAuthProvider";
import { Button } from "@/components/ui/button";
import { MULTIVARIATE_FEATURE_FLAGS } from "@/lib/feature-flags";
import {
  type HomepageEntryVariant,
  parseHomepageEntryVariant,
  withHomepageEntryVariant,
} from "@/lib/homepage-entry-experiment";
import Link, { type LinkProps } from "@/lib/locale/Link";

const RESOLUTION_WINDOW_MS = 500;

type EntryDecision = {
  exposed: boolean;
  variant: HomepageEntryVariant;
};

type HomepageEntryContextValue = {
  authState: PublicAuthState;
  decision: EntryDecision | null;
};

const HomepageEntryContext = createContext<HomepageEntryContextValue | undefined>(undefined);

export const useHomepageEntryVariant = () => {
  const context = useContext(HomepageEntryContext);
  return context?.authState === "signed-out" && context.decision?.exposed
    ? context.decision.variant
    : undefined;
};

export function HomepageEntryExperimentProvider({ children }: { children: ReactNode }) {
  const posthog = usePostHog();
  const authState = usePublicAuthState();
  const [decision, setDecision] = useState<EntryDecision | null>(null);
  const decidedRef = useRef(false);
  const flag = MULTIVARIATE_FEATURE_FLAGS.homepageTranscriptionEntry;

  useEffect(() => {
    const decide = (value: unknown) => {
      if (decidedRef.current) return;
      decidedRef.current = true;
      const variant = parseHomepageEntryVariant(value);
      setDecision({ exposed: !!variant, variant: variant ?? flag.default });
    };

    const timeout = window.setTimeout(() => decide(undefined), RESOLUTION_WINDOW_MS);
    const unsubscribe = posthog.onFeatureFlags((_flags, variants, context) => {
      if (context?.errorsLoading) {
        decide(undefined);
        return;
      }
      decide(variants[flag.key]);
    });

    return () => {
      window.clearTimeout(timeout);
      unsubscribe();
    };
  }, [flag.default, flag.key, posthog]);

  useEffect(() => {
    if (authState !== "signed-out" || !decision?.exposed) return;

    // Resolve without telemetry first so a flag change between the callback and
    // this post-paint effect cannot report a variant that was never rendered.
    const currentVariant = posthog.getFeatureFlag(flag.key, { send_event: false });
    if (currentVariant === decision.variant) {
      posthog.getFeatureFlag(flag.key);
    }
  }, [authState, decision, flag.key, posthog]);

  return (
    <HomepageEntryContext.Provider value={{ authState, decision }}>
      {children}
    </HomepageEntryContext.Provider>
  );
}

export function HomepageHeroEntryExperiment({
  control,
  ctaLabel,
}: {
  control: ReactNode;
  ctaLabel: string;
}) {
  const context = useContext(HomepageEntryContext);

  if (
    !context?.decision ||
    (context.decision.variant === "control" && context.authState === "loading")
  ) {
    return <div aria-hidden="true" className="min-h-24" data-testid="homepage-entry-pending" />;
  }

  if (context.decision.variant === "control" && context.authState === "signed-in") return null;

  if (context.decision.variant === "cta") {
    return (
      <div data-testid="homepage-entry-cta">
        <div className="flex justify-center pt-2">
          <Button asChild size="lg" className="w-full text-xl sm:w-auto sm:px-12 sm:py-6">
            <HomepageExperimentSignUpLink href="/app/auth/sign-up?utm_source=landing&utm_medium=cta&utm_campaign=main_cta">
              {ctaLabel}
            </HomepageExperimentSignUpLink>
          </Button>
        </div>
      </div>
    );
  }

  return <div data-testid="homepage-entry-control">{control}</div>;
}

export function HomepageExperimentSignUpLink({
  href,
  ...props
}: Omit<ComponentProps<typeof Link>, "href"> & { href: LinkProps["href"] }) {
  const homepageEntryVariant = useHomepageEntryVariant();
  const attributedHref =
    typeof href === "string" ? withHomepageEntryVariant(href, homepageEntryVariant) : href;

  return <Link href={attributedHref} data-homepage-experiment-signup="" {...props} />;
}
